import { type CSSProperties, type ComponentType, useId, useState } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { HiShoppingBag } from 'react-icons/hi';
import { TbReportMoney } from 'react-icons/tb';
import { views } from '@/copy';
import { AiGeneratedLabel } from '@/ui/design-system/components';
import { TopPostTikTokThumbnail } from '@/ui/components/Post/Post';
import { toneClass } from '../lib/metricTone';
import VisualTooltip from './VisualTooltip';
import { ColumnHeaderRow, ShowMoreButton } from './tableChrome';
import InlineTableHeader from './InlineTableHeader';
import AssistantVisualFrame from './AssistantVisualFrame';
import type {
    Breadcrumb,
    Column,
    DashboardUrl,
    TableTitleModel,
    Tone,
    VisualFrameProps,
} from '../types';

export interface PostMetricRowModel {
    label: string;
    value: string | null;
    tone?: Tone;
}

export interface PostBadgesModel {
    /** Sentiment bucket chip: the dashboard's emotion icon on the score color.
     * The tooltip shows the bucket `title` plus the post's text `summary`. */
    sentiment?: {
        Icon: ComponentType<{ size?: number | string }>;
        color?: string;
        title?: string;
        summary?: string;
    };
    isNew?: boolean;
    isPaid?: boolean;
    /** Paid-types detail, shown in the paid chip's tooltip. */
    paidTitle?: string;
    isShop?: boolean;
    shopTitle?: string;
}

export interface PostCardModel {
    url?: string;
    badges: PostBadgesModel;
    rows: PostMetricRowModel[];
}

export interface PostGridModel {
    breadcrumb: Breadcrumb;
    title: TableTitleModel;
    link?: DashboardUrl;
    columns: Column[];
    cards: PostCardModel[];
}

// Figma "Tiktok posts V2" (node 267:7854): ~3:4 thumbnail ≈ 158px tall at the
// grid's 3-column width; the reused dashboard thumbnail sizes itself via this var.
const CARD_STYLE = { '--post-thumbnail-height': '9.875rem' } as CSSProperties;

// Two grid rows visible before the "Show N more" expander (node 267:7276).
const MAX_VISIBLE = 6;

// Translucent backdrop-blur chip overlaying the thumbnail (nodes 267:7903/7910).
const CHIP =
    'pointer-events-auto inline-flex h-5 items-center justify-center gap-1 rounded-md px-2 text-xs text-white backdrop-blur-md';

// Sentiment tooltip: the bucket title, plus the post's text summary when the
// store has one (the mini equivalent of the dashboard sentiment badge's body).
const sentimentTooltip = (
    sentiment: NonNullable<PostBadgesModel['sentiment']>,
) =>
    sentiment.title || sentiment.summary ? (
        <div className="flex flex-col gap-1">
            {sentiment.title && (
                <span className="font-medium">{sentiment.title}</span>
            )}
            {sentiment.summary && (
                <>
                    <AiGeneratedLabel tone="inverse" />
                    <span>{sentiment.summary}</span>
                </>
            )}
        </div>
    ) : undefined;

// Sentiment (top-left) and Paid (top-right) ride the top corners; the rarer
// New / TikTok Shop chips sit along the bottom edge. pointer-events-none lets
// clicks fall through to the thumbnail's post link; the chips re-enable them so
// their tooltips still open.
const BadgeOverlay = ({ badges }: { badges: PostBadgesModel }) => (
    <>
        <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-1 p-2 pointer-events-none">
            <span className="flex items-center gap-1">
                {badges.sentiment && (
                    <VisualTooltip content={sentimentTooltip(badges.sentiment)}>
                        <span
                            className={CHIP}
                            tabIndex={0}
                            role="button"
                            aria-label="Sentiment"
                            // The bucket is double-encoded: the dashboard's emotion
                            // icon + its score color as the chip background, at the
                            // New chip's translucency so the blur reads through.
                            style={
                                badges.sentiment.color
                                    ? {
                                          backgroundColor: `color-mix(in srgb, ${badges.sentiment.color} 35%, transparent)`,
                                      }
                                    : undefined
                            }
                        >
                            <badges.sentiment.Icon
                                size={12}
                                aria-hidden="true"
                            />
                        </span>
                    </VisualTooltip>
                )}
            </span>
            <span className="flex items-center gap-1">
                {badges.isPaid && (
                    <VisualTooltip content={badges.paidTitle}>
                        <span
                            className={`${CHIP} bg-white/35`}
                            tabIndex={0}
                            role="button"
                            aria-label="Paid"
                        >
                            <TbReportMoney size={12} aria-hidden="true" />
                        </span>
                    </VisualTooltip>
                )}
            </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-end gap-1 p-2 pointer-events-none">
            {badges.isNew && (
                <span className={`${CHIP} bg-badge-new-overlay`}>New</span>
            )}
            {badges.isShop && (
                <VisualTooltip content={badges.shopTitle}>
                    <span
                        className={`${CHIP} bg-white/35`}
                        tabIndex={0}
                        role="button"
                        aria-label="Shop"
                    >
                        <HiShoppingBag size={12} aria-hidden="true" />
                    </span>
                </VisualTooltip>
            )}
        </div>
    </>
);

const PostCard = ({ card }: { card: PostCardModel }) => (
    <div className="flex flex-col gap-3" style={CARD_STYLE}>
        <div className="relative w-full overflow-hidden rounded-md">
            <TopPostTikTokThumbnail url={card.url} />
            <BadgeOverlay badges={card.badges} />
        </div>
        <div className="flex flex-col gap-2">
            {card.rows.map((row) => (
                <div
                    key={row.label}
                    className="flex items-center justify-between gap-2 text-xs"
                >
                    <span className="text-muted-foreground truncate">
                        {row.label}
                    </span>
                    {/* uppercase capitalizes numbro's volume suffixes (946.1k →
                        946.1K), matching the dashboard card + table cells. */}
                    <span className="inline-flex items-center gap-1 font-medium text-foreground whitespace-nowrap uppercase">
                        {row.value ?? '—'}
                        {row.tone === 'up' && (
                            <ArrowUpRight
                                size={12}
                                aria-hidden="true"
                                className={toneClass('up')}
                            />
                        )}
                        {row.tone === 'down' && (
                            <ArrowDownRight
                                size={12}
                                aria-hidden="true"
                                className={toneClass('down')}
                            />
                        )}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

/**
 * Vertically-expanding grid of TikTok post cards (Figma "Tiktok posts V2",
 * node 267:6697): InlineTable-style chrome (title / "View all" / column header /
 * "Show N more"), a 3-column card grid, badges overlaid on the reused dashboard
 * thumbnail, and label/value metric rows per card with growth-tone arrows.
 */
const InlinePostGrid = ({
    breadcrumb,
    title,
    link,
    columns,
    cards,
    maxVisible = MAX_VISIBLE,
    Frame = AssistantVisualFrame,
}: PostGridModel & {
    maxVisible?: number;
    Frame?: ComponentType<VisualFrameProps>;
}) => {
    const [expanded, setExpanded] = useState(false);
    const gridId = useId();
    const hiddenCount = cards.length - maxVisible;
    const visibleCards = expanded ? cards : cards.slice(0, maxVisible);

    return (
        <Frame breadcrumb={breadcrumb}>
            <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
                <InlineTableHeader title={title} link={link} />
                <ColumnHeaderRow nameColumnHeader="Post" columns={columns} />
                {cards.length === 0 ? (
                    <div className="px-3.5 py-4 text-xs text-muted-foreground text-center">
                        {views.assistantVisuals.noRows}
                    </div>
                ) : (
                    <div id={gridId} className="grid grid-cols-3 gap-3 p-4">
                        {visibleCards.map((card, i) => (
                            <PostCard key={card.url ?? i} card={card} />
                        ))}
                    </div>
                )}
                {hiddenCount > 0 && (
                    <ShowMoreButton
                        expanded={expanded}
                        hiddenCount={hiddenCount}
                        controlsId={gridId}
                        onToggle={() => setExpanded((v) => !v)}
                    />
                )}
            </div>
        </Frame>
    );
};

export default InlinePostGrid;
